import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useConsole } from '@/lib/store'
import { copy } from '@/copy'

// A row that signs something out: its action, then an inline question that
// names what stops, with Cancel focused (D-014), and a line if it failed.
export function SignOutRow({
  name,
  action,
  ask,
  failedLine,
  onConfirm,
  children,
  columns,
}: {
  name: string
  action: string
  ask: string
  failedLine: string
  onConfirm: () => Promise<boolean>
  children: ReactNode
  columns: string
}) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const cancel = useRef<HTMLButtonElement>(null)
  const open = useRef<HTMLButtonElement>(null)
  const hold = useConsole((s) => s.hold)
  const release = useConsole((s) => s.release)

  useEffect(() => {
    if (!asking) return
    hold()
    cancel.current?.focus()
    return release
  }, [asking, hold, release])

  const close = () => {
    setAsking(false)
    requestAnimationFrame(() => open.current?.focus())
  }
  const confirm = async () => {
    if (busy) return
    setBusy(true)
    const ok = await onConfirm()
    setBusy(false)
    if (!ok) {
      setFailed(true)
      close()
    }
  }
  const askId = `ask-${name.replace(/\W+/g, '-')}`

  return (
    <li className="m-0 border-t border-rule px-5 py-4 first:border-t-0 min-[768px]:py-3">
      <div className={`grid grid-cols-1 items-center gap-x-6 gap-y-2 ${columns}`}>
        {children}
        <div className="flex justify-start min-[768px]:justify-end">
          {!asking && (
            <button type="button" className="action w-full min-[768px]:w-auto" ref={open} onClick={() => (setFailed(false), setAsking(true))}>
              {action}
              <span className="visually-hidden"> {name}</span>
            </button>
          )}
        </div>
      </div>
      {asking && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 rounded-[6px] border border-rule bg-sunken p-3" onKeyDown={(e) => e.key === 'Escape' && close()}>
          <p id={askId} className="m-0 mr-auto font-medium">
            {ask}
          </p>
          <button type="button" className="button flex-1 min-[560px]:flex-none" onClick={confirm} aria-describedby={askId} aria-disabled={busy || undefined}>
            {busy ? copy.signingOut : copy.signOut}
            <span className="visually-hidden"> {name}</span>
          </button>
          <button type="button" className="action flex-1 min-[560px]:flex-none" ref={cancel} onClick={close}>
            {copy.cancel}
          </button>
        </div>
      )}
      {failed && (
        <p className="m-0 mt-2 text-right text-[14px] leading-5 text-failed" role="status">
          {failedLine}
        </p>
      )}
    </li>
  )
}

/** One fact in a row: its label is the column head on a wide screen, and beside the value on a narrow one. */
export function Cell({ label, children, numeric }: { label: string; children: ReactNode; numeric?: boolean }) {
  return (
    <div className={`flex min-w-0 items-baseline justify-between gap-4 min-[768px]:block ${numeric ? 'min-[768px]:text-right' : ''}`}>
      <span className="text-[14px] text-secondary min-[768px]:visually-hidden">{label}</span>
      <span className={numeric ? 'font-mono tabular-nums' : ''}>{children}</span>
    </div>
  )
}

/** The column heads, on a wide screen only; each row's cells carry their own labels too. */
export function Heads({ labels, columns }: { labels: { text: string; numeric?: boolean }[]; columns: string }) {
  return (
    <div className={`hidden items-center gap-x-6 border-b border-rule px-5 py-2.5 text-[14px] text-secondary min-[768px]:grid ${columns}`} aria-hidden="true">
      {labels.map((l, i) => (
        <span key={i} className={l.numeric ? 'text-right' : ''}>
          {l.text}
        </span>
      ))}
      <span />
    </div>
  )
}
